/**
 * Progress Indicators and Loading States
 * 
 * Enhanced progress indicators for better perceived performance and user feedback
 * during long-running operations and data loading.
 */

import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import { ArrowPathIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid'

// ===== Basic Progress Components =====

export interface ProgressBarProps {
  progress: number // 0-100
  size?: 'sm' | 'md' | 'lg'
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'indigo'
  showPercentage?: boolean
  animated?: boolean
  className?: string
  label?: string
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  size = 'md',
  color = 'blue',
  showPercentage = false,
  animated = true,
  className = '',
  label
}) => {
  const clampedProgress = Math.max(0, Math.min(100, progress))

  const sizeClasses = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4'
  }

  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    indigo: 'bg-indigo-500'
  }

  return (
    <div className={clsx('w-full', className)}>
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-2">
          {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
          {showPercentage && (
            <span className="text-sm text-gray-500">{Math.round(clampedProgress)}%</span>
          )}
        </div>
      )}
      <div className={clsx(
        'w-full bg-gray-200 rounded-full overflow-hidden',
        sizeClasses[size]
      )}>
        <div
          className={clsx(
            'h-full rounded-full transition-all duration-300 ease-out',
            colorClasses[color],
            animated && 'transition-transform'
          )}
          style={{ width: `${clampedProgress}%` }}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label || `Progress: ${Math.round(clampedProgress)}%`}
        />
      </div>
    </div>
  )
}

// ===== Circular Progress Indicator =====

export interface CircularProgressProps {
  progress: number // 0-100
  size?: number // diameter in pixels
  strokeWidth?: number
  color?: string
  backgroundColor?: string
  showPercentage?: boolean
  className?: string
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  size = 40,
  strokeWidth = 4,
  color = '#3B82F6',
  backgroundColor = '#E5E7EB',
  showPercentage = false,
  className = ''
}) => {
  const clampedProgress = Math.max(0, Math.min(100, progress))
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const strokeDasharray = circumference
  const strokeDashoffset = circumference - (clampedProgress / 100) * circumference

  return (
    <div className={clsx('relative inline-flex items-center justify-center', className)}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
        role="progressbar"
        aria-valuenow={clampedProgress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-300 ease-out"
        />
      </svg>
      {showPercentage && (
        <span className="absolute text-xs font-medium text-gray-700">
          {Math.round(clampedProgress)}%
        </span>
      )}
    </div>
  )
}

// ===== Animated Loading Dots =====

export interface LoadingDotsProps {
  size?: 'sm' | 'md' | 'lg'
  color?: string
  className?: string
}

export const LoadingDots: React.FC<LoadingDotsProps> = ({
  size = 'md',
  color = 'bg-gray-400',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-1 h-1',
    md: 'w-2 h-2',
    lg: 'w-3 h-3'
  }

  return (
    <div className={clsx('flex space-x-1', className)} role="status" aria-label="Loading">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className={clsx(
            'rounded-full animate-pulse',
            sizeClasses[size],
            color
          )}
          style={{
            animationDelay: `${index * 0.2}s`,
            animationDuration: '1s'
          }}
        />
      ))}
    </div>
  )
}

// ===== Step Progress Indicator =====

export interface StepProgressProps {
  steps: Array<{
    id: string
    label: string
    description?: string
  }>
  currentStep: number // 0-based index
  completedSteps?: number[] // array of completed step indices
  className?: string
}

export const StepProgress: React.FC<StepProgressProps> = ({
  steps,
  currentStep,
  completedSteps = [],
  className = ''
}) => {
  return (
    <nav aria-label="Progress" className={className}>
      <ol className="flex items-center">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(index)
          const isCurrent = index === currentStep
          const isUpcoming = index > currentStep

          return (
            <li key={step.id} className={clsx(
              'relative',
              index !== steps.length - 1 && 'pr-8 sm:pr-20'
            )}>
              {/* Connector line */}
              {index !== steps.length - 1 && (
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className={clsx(
                    'h-0.5 w-full',
                    isCompleted || (isCurrent && index > 0) ? 'bg-indigo-600' : 'bg-gray-200'
                  )} />
                </div>
              )}

              {/* Step indicator */}
              <div className="relative flex items-center justify-center">
                {isCompleted ? (
                  <CheckCircleIcon className="w-8 h-8 text-indigo-600" aria-hidden="true" />
                ) : isCurrent ? (
                  <div className="flex items-center justify-center w-8 h-8 border-2 border-indigo-600 rounded-full bg-white">
                    <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-8 h-8 border-2 border-gray-300 rounded-full bg-white">
                    <div className="w-2.5 h-2.5 bg-transparent rounded-full" />
                  </div>
                )}

                {/* Step label */}
                <div className="ml-4 min-w-0">
                  <p className={clsx(
                    'text-sm font-medium',
                    isCompleted || isCurrent ? 'text-indigo-600' : 'text-gray-500'
                  )}>
                    {step.label}
                  </p>
                  {step.description && (
                    <p className="text-sm text-gray-500">{step.description}</p>
                  )}
                </div>
              </div>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

// ===== Batch Operation Progress =====

export interface BatchProgressProps {
  total: number
  completed: number
  failed?: number
  label?: string
  showDetails?: boolean
  estimatedTimeRemaining?: number
  className?: string
}

export const BatchProgress: React.FC<BatchProgressProps> = ({
  total,
  completed,
  failed = 0,
  label = 'Processing',
  showDetails = true,
  estimatedTimeRemaining,
  className = ''
}) => {
  const progress = total > 0 ? (completed / total) * 100 : 0
  const remaining = total - completed - failed

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${Math.round(seconds / 3600)}h`
  }

  return (
    <div className={clsx('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {estimatedTimeRemaining && estimatedTimeRemaining > 0 && (
          <span className="text-sm text-gray-500">
            ~{formatTime(estimatedTimeRemaining)} remaining
          </span>
        )}
      </div>

      <ProgressBar
        progress={progress}
        color={failed > 0 ? 'yellow' : 'blue'}
        animated={true}
      />

      {showDetails && (
        <div className="flex justify-between text-sm text-gray-600">
          <div className="flex space-x-4">
            <span>✓ {completed} completed</span>
            {failed > 0 && <span className="text-red-600">✗ {failed} failed</span>}
            {remaining > 0 && <span>⏳ {remaining} remaining</span>}
          </div>
          <span>{completed + failed} / {total}</span>
        </div>
      )}
    </div>
  )
}

// ===== Skeleton Loading Components =====

export interface SkeletonProps {
  width?: string | number
  height?: string | number
  className?: string
  rounded?: boolean
  animated?: boolean
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '1rem',
  className = '',
  rounded = true,
  animated = true
}) => {
  const widthStyle = typeof width === 'number' ? `${width}px` : width
  const heightStyle = typeof height === 'number' ? `${height}px` : height

  return (
    <div
      className={clsx(
        'bg-gray-200',
        rounded && 'rounded',
        animated && 'animate-pulse',
        className
      )}
      style={{ width: widthStyle, height: heightStyle }}
      role="status"
      aria-label="Loading content"
    />
  )
}

export const SkeletonText: React.FC<{
  lines?: number
  className?: string
}> = ({ lines = 3, className = '' }) => {
  return (
    <div className={clsx('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          height="0.75rem"
          width={index === lines - 1 ? '75%' : '100%'}
        />
      ))}
    </div>
  )
}

// ===== Loading State Wrapper =====

export interface LoadingStateProps {
  isLoading: boolean
  error?: Error | null
  children: React.ReactNode
  loadingComponent?: React.ReactNode
  errorComponent?: React.ReactNode
  retryAction?: () => void
  className?: string
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  isLoading,
  error,
  children,
  loadingComponent,
  errorComponent,
  retryAction,
  className = ''
}) => {
  if (error) {
    if (errorComponent) {
      return <>{errorComponent}</>
    }

    return (
      <div className={clsx('text-center py-8', className)}>
        <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
        <p className="text-gray-600 mb-4">{error.message}</p>
        {retryAction && (
          <button
            onClick={retryAction}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Try Again
          </button>
        )}
      </div>
    )
  }

  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>
    }

    return (
      <div className={clsx('text-center py-8', className)}>
        <ArrowPathIcon className="mx-auto h-8 w-8 animate-spin text-blue-500 mb-4" />
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  return <>{children}</>
}

// ===== Inline Loading Button =====

export interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean
  loadingText?: string
  children: React.ReactNode
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  isLoading = false,
  loadingText = 'Loading...',
  children,
  disabled,
  className = '',
  ...props
}) => {
  return (
    <button
      {...props}
      disabled={disabled || isLoading}
      className={clsx(
        'inline-flex items-center justify-center',
        isLoading && 'cursor-not-allowed opacity-75',
        className
      )}
    >
      {isLoading && (
        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
      )}
      {isLoading ? loadingText : children}
    </button>
  )
}

export default {
  ProgressBar,
  CircularProgress,
  LoadingDots,
  StepProgress,
  BatchProgress,
  Skeleton,
  SkeletonText,
  LoadingState,
  LoadingButton
}